import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  AlertTriangle,
  Check,
  Copy,
  Database,
  LifeBuoy,
  Loader2,
  Terminal,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useClipboard } from "use-clipboard-copy";
import {
  createDisasterRecoveryOTP,
  getDisasterRecoveryReadiness,
  type DisasterRecoveryOTP,
  type DisasterRecoveryReadiness,
} from "@/data/automation-server";

// Rebuilding a lost server onto fresh hardware needs an AOC access token on a
// machine where nothing exists yet — and that token doubles as the password to
// the server's backup repository, so it also decides which bucket the CLI
// reaches. This dialog mints a recovery OTP against the *existing* server
// record, which is what makes "the right bucket" a property of the flow rather
// than something the operator has to get right by hand.
//
// Two things it has to be honest about:
//   * the restic key is never escrowed, so we cannot hand it over — the
//     operator must bring their own copy or the backup is unreadable;
//   * redeeming the OTP replaces the server's token, so recovering a server
//     that is still alive cuts it off from the AOC. Minting is harmless, and so
//     is starting the recovery: the command proves the operator's key opens the
//     repository and prints the restore plan before it asks anything. The swap
//     happens only when they confirm, so a wrong key costs neither the OTP nor
//     the live server's token.

interface DisasterRecoveryModalProps {
  children?: React.ReactNode;
  serverName: string;
  serverId: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function fmtWhen(iso: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function errorMessage(error: any, fallback: string) {
  if (error?.response?.status === 403) {
    return "You don't have permission to start disaster recovery for this server. Only admin users in the same organization can.";
  }
  return error?.response?.data?.error ?? error?.message ?? fallback;
}

export function DisasterRecoveryModal({
  children,
  serverName,
  serverId,
  open: controlledOpen,
  onOpenChange,
}: DisasterRecoveryModalProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const [readiness, setReadiness] = useState<DisasterRecoveryReadiness | null>(null);
  const [issued, setIssued] = useState<DisasterRecoveryOTP | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isIssuing, setIsIssuing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const clipboard = useClipboard({
    onSuccess() {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    },
  });

  const loadReadiness = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setReadiness(await getDisasterRecoveryReadiness(serverId));
    } catch (e: any) {
      setError(errorMessage(e, "Could not check whether this server can be recovered"));
    }
    setIsLoading(false);
  }, [serverId]);

  useEffect(() => {
    if (controlledOpen) loadReadiness();
  }, [controlledOpen, loadReadiness]);

  const handleOpenChange = async (newOpen: boolean) => {
    setUncontrolledOpen(newOpen);
    onOpenChange?.(newOpen);
    if (!newOpen) {
      setReadiness(null);
      setIssued(null);
      setError(null);
      return;
    }
    if (controlledOpen === undefined) await loadReadiness();
  };

  const issueOTP = async () => {
    setIsIssuing(true);
    setError(null);
    try {
      const result = await createDisasterRecoveryOTP(serverId);
      setIssued(result);
      setReadiness(result);
    } catch (e: any) {
      setError(errorMessage(e, "Could not start disaster recovery"));
    }
    setIsIssuing(false);
  };

  const repo = readiness?.repo;
  const last = readiness?.last_request;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-amber-600" />
            Disaster recovery
          </DialogTitle>
          <DialogDescription>
            Rebuild <strong>{serverName}</strong>, everything running on it, its
            secrets and configuration, onto a replacement machine from its backups. The new
            machine needs only Docker and network access.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Checking this server&apos;s backups…
            </div>
          )}

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {repo && (
            <div className="rounded-lg border bg-muted/40 p-3.5 text-sm">
              <div className="mb-2 flex items-center gap-2 font-medium">
                <Database className="h-4 w-4" />
                Backup repository
              </div>
              <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-[13px]">
                <dt className="text-muted-foreground">Bucket</dt>
                <dd className="break-all font-mono">{repo.bucket ?? "none"}</dd>
                <dt className="text-muted-foreground">Snapshots</dt>
                <dd>{repo.snapshot_count}</dd>
                <dt className="text-muted-foreground">Newest</dt>
                <dd>{fmtWhen(repo.newest_snapshot_at)}</dd>
                {/* Which build the rebuilt server will run. Shown before the OTP
                    is issued, so "the exact version can't be reproduced" is
                    something the operator learns while they still have options —
                    not from a command already pasted onto a bare machine. */}
                <dt className="text-muted-foreground">Version</dt>
                <dd>
                  {readiness?.pinned_version ? (
                    <span className="font-mono">{readiness.pinned_version}</span>
                  ) : (
                    <span className="font-mono">
                      {readiness?.reported_version ?? "unknown"}
                    </span>
                  )}
                  {!readiness?.pinned_version && (
                    <span className="ml-1.5 text-muted-foreground">
                      &rarr; newest release
                    </span>
                  )}
                </dd>
              </dl>
              {readiness?.version_note && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {readiness.version_note}.
                </p>
              )}
            </div>
          )}

          {readiness?.blockers.map((blocker) => (
            <div
              key={blocker}
              className="flex gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{blocker}</span>
            </div>
          ))}

          {readiness?.warnings.map((warning) => (
            <div
              key={warning}
              className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{warning}</span>
            </div>
          ))}

          {last && !issued && (
            <p className="text-xs text-muted-foreground">
              Recovery was last requested by {last.requested_by} on{" "}
              {fmtWhen(last.requested_at)} —{" "}
              {last.status === "exchanged"
                ? `redeemed ${fmtWhen(last.exchanged_at)}`
                : last.status === "pending"
                  ? "not yet redeemed"
                  : "never redeemed, expired"}
              .
            </p>
          )}

          {readiness && !issued && (
            <>
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <strong>You need the backup encryption key.</strong> It is never
                stored here or in object storage — only on the server itself and
                in the copy you saved. Without it the backups cannot be read, and
                no recovery flow can help.
              </div>
              <Button
                onClick={issueOTP}
                disabled={isIssuing || !readiness.recoverable}
                className="w-full bg-amber-600 hover:bg-amber-700"
              >
                {isIssuing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Starting recovery…
                  </>
                ) : (
                  "Get the recovery command"
                )}
              </Button>
            </>
          )}

          {issued && (
            <div className="space-y-3 rounded-lg border bg-muted/40 p-3.5">
              <div className="flex items-center gap-2">
                <Terminal className="h-4 w-4" />
                <span className="text-sm font-medium">
                  Run this on the replacement machine
                </span>
              </div>
              <div className="flex items-stretch overflow-hidden rounded-md border bg-background">
                <code className="min-w-0 flex-1 whitespace-pre-wrap break-all px-3 py-2 font-mono text-[13px] leading-5">
                  {issued.command}
                </code>
                <button
                  type="button"
                  title="Copy"
                  onClick={() => clipboard.copy(issued.command)}
                  className="flex w-10 shrink-0 items-center justify-center self-stretch border-l text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  {copied ? (
                    <Check className="h-[15px] w-[15px] text-green-600" />
                  ) : (
                    <Copy className="h-[15px] w-[15px]" />
                  )}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                It will ask for your copy of the backup encryption key — have it
                to hand before you start. The one-time password expires{" "}
                {fmtWhen(issued.expires_at)}; request a new one if it lapses.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
